import React, { useMemo } from 'react'
import { Button } from '../../../components/shadcn/Button'
import { useCore } from '../../../hooks'
import { ControlledSelect } from '../../../components/Form/Controlled/ControlledSelect'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { ControlledInput } from '../../../components/Form/Controlled/ControlledInput'
import { ControlledCheckbox } from '../../../components/Form/Controlled/ControlledCheckbox'
import { useBasicEntities } from '../../../hooks/useBasicEntities'
import { graphql, useFragment } from '../../../gql'
import { useGQLMutation } from '../../../hooks/GQLUtilityHooks'
import { toastError, toastSuccess } from '../../../lib/toastHelpers'
import { useQueryClient } from '@tanstack/react-query'
import { useOrderDetailContext } from '../OrderDetailContext'
import { Loader } from '../../../components/Loader'
import { Carrier } from '../../../gql/graphql'
import { formatCurrency } from '../../../components/OrderPrice'

const deletePaymentItemMutation = graphql(`
  mutation DeletePaymentItem($id: Int!) {
    deletePaymentItem(id: $id) {
      errors
    }
  }
`)

export const CarrierCurrencyPayment_OrderFragment = graphql(`
  fragment CarrierCurrencyPayment_OrderFragment on Order {
    id
    currency {
      id
    }
    paymentItems {
      id
      payment {
        id
        name
      }
      amount
      isPaid
    }
  }
`)

export const CarrierCurrencyPayment: React.FC = () => {
  const { carriers, currencies, payments } = useBasicEntities()

  const { control } = useFormContext<{
    paymentItems: {
      payment: string
      paymentItemId: string
      amount: number
      isPaid: boolean
    }[]
    carrier: string
    currency: string
  }>()
  const queryClient = useQueryClient()
  const context = useOrderDetailContext()
  const order = useFragment(CarrierCurrencyPayment_OrderFragment, context.order)

  // We need to make a separete request to delete payment item, so not to complicate the form login even more, let's make it here right away.
  const {
    mutateAsync: deletePaymentItem,
    variables,
    isPending,
  } = useGQLMutation(deletePaymentItemMutation, ['deletePaymentItem'], {
    onSuccess: (_, { id }) => {
      toastSuccess('Platební metoda byla smazána')
      queryClient.setQueryData(['order', { id: order.id }], (old: any) => {
        return {
          order: {
            ...old.order,
            data: {
              ...old.order.data,
              paymentItems: old.order.data.paymentItems.filter((pi: any) => pi.id !== id),
            },
          },
        }
      })

      // Remove the payment item from the form
      remove(fields.findIndex(field => field?.paymentItemId && field.paymentItemId.toString() === id.toString()))
    },
    onError: () => {
      toastError('Platební metoda nebyla smazána')
    },
  })

  const { fields, append, prepend, remove, swap, move, insert } = useFieldArray({
    control,
    name: 'paymentItems',
  })

  const addPaymentItem = () => {
    append({ payment: payments[0].id?.toString(), amount: 0, isPaid: true, paymentItemId: '' })
  }

  const carrierOptions = useMemo(() => {
    return [
      { label: 'Vyberte dopravce', value: null },
      ...carriers
        .reduce<Carrier[]>((ac, el) => {
          if (ac.find(c => c.id === el.id)) {
            return ac
          }
          return [...ac, el]
        }, [])
        .map(c => ({ label: c.name, value: c.id })),
    ]
  }, [carriers])

  return (
    <div className={'flex flex-col gap-4'}>
      <div className="flex gap-3 justify-between">
        <div className={'box flex flex-col gap-3 flex-1'}>
          <h1 className={'text-black text-base font-semibold'}>Dopravce</h1>
          <div className={'flex gap-3'}>
            <ControlledSelect className={'flex-1'} name={'carrier'} options={carrierOptions} control={control} />
          </div>
        </div>

        <div className={'box flex flex-col gap-3 flex-1'}>
          <h1 className={'text-black text-base font-semibold'}>Měna</h1>
          <div className={'flex gap-3'}>
            <ControlledSelect
              className={'flex-1'}
              options={(currencies || []).map(c => ({ label: c.name, value: c.id }))}
              control={control}
              name={'currency'}
            />
          </div>
        </div>
      </div>

      <div className={'box flex flex-col gap-3'}>
        <div className={'flex justify-between items-center'}>
          <h1 className={'text-black text-base font-semibold'}>Platební metoda</h1>
          <Button variant="outline" icon={['far', 'plus']} title={'Přidat platební metodu'} onClick={addPaymentItem}>
            {'Přidat platební metodu'}
          </Button>
        </div>
        {fields.map((field, index) => (
          <div className={'flex items-center gap-3'} key={`${field.id}`}>
            <ControlledSelect
              className={'flex-1'}
              options={(payments || []).map(p => ({ label: p.name, value: p.id.toString() }))}
              control={control}
              name={`paymentItems.${index}.payment`}
            />
            <div className={'self-end flex-1'}>
              <ControlledInput
                type={'number'}
                inputClassName={'text-right'}
                name={`paymentItems.${index}.amount`}
                control={control}
                right={formatCurrency(order.currency)}
              />
            </div>
            <div className={'self-end'}>
              <ControlledCheckbox label={'zaplaceno'} name={`paymentItems.${index}.isPaid`} control={control} />
            </div>
            <Controller
              name={`paymentItems.${index}.paymentItemId`}
              control={control}
              render={({ field }) => (
                <div className={'self-end'}>
                  <Button
                    variant="destructive"
                    size="circle"
                    icon={isPending && variables?.id === Number(field.value) ? undefined : ['far', 'trash-alt']}
                    title={'Smazat'}
                    disabled={isPending}
                    onClick={() => {
                      if (field.value) {
                        deletePaymentItem({ id: Number(field.value) })
                      } else {
                        remove(index)
                      }
                    }}>
                    {isPending && variables?.id === Number(field.value) ? (
                      <div className="loader-nano ease-linear loader-white"></div>
                    ) : null}
                  </Button>
                </div>
              )}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
